import type { ActivityItem, BoostSummary, Candle, LaunchSummary, Paginated, ProtocolStats } from "@bubblefun/shared";
import { demoActivity, demoBoosts, demoCandles, demoLaunches, demoStats } from "../data/demo";

const configuredApi = import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";
const baseUrl = configuredApi || "";

export interface HolderPosition {
  address: `0x${string}`;
  percentage: number;
}

interface HolderSnapshot {
  items: HolderPosition[];
  total: number;
  source: "indexed" | "demo";
}

const demoHolderPositions: HolderPosition[] = [
  { address: "0x65d18198e86bc81D60AC297EA057c072F42C1B26", percentage: 12.84 },
  { address: "0xe46e3C12B5E2e2677afcb920aB28227972681611", percentage: 8.21 },
  { address: "0x408c50A7bE50D88Af8563B9260f9C02028DF2B47", percentage: 5.76 },
  { address: "0xF033Db6c8B685e18c8e3187A7F0cb1228A607E44", percentage: 3.49 },
  { address: "0x1c9AAe0C97B29E8aAE74b13210B0A26fb48dA283", percentage: 2.18 }
];

async function getJson<T>(path: string, fallback: T): Promise<{ data: T; demo: boolean }> {
  try {
    const response = await fetch(`${baseUrl}${path}`, { headers: { accept: "application/json" } });
    if (!response.ok) throw new Error(`API ${response.status}`);
    return { data: (await response.json()) as T, demo: false };
  } catch {
    return { data: fallback, demo: true };
  }
}

export async function getLaunches(): Promise<{ launches: LaunchSummary[]; demo: boolean }> {
  const result = await getJson<Paginated<LaunchSummary>>("/api/launches?limit=40", {
    items: demoLaunches,
    cursor: null,
    source: "demo"
  });
  return { launches: result.data.items, demo: result.demo || result.data.source === "demo" };
}

export async function getStats(): Promise<{ stats: ProtocolStats; demo: boolean }> {
  const result = await getJson<ProtocolStats>("/api/stats", demoStats);
  return { stats: result.data, demo: result.demo };
}

export async function getBoosts(): Promise<{ boosts: BoostSummary[]; demo: boolean }> {
  const result = await getJson<Paginated<BoostSummary>>("/api/boosts?limit=12", {
    items: demoBoosts,
    cursor: null,
    source: "demo"
  });
  return { boosts: result.data.items, demo: result.demo || result.data.source === "demo" };
}

export interface CandleQuery {
  interval?: "5m";
  from?: number;
}

export async function getCandleSnapshot(address: string, query: CandleQuery = {}): Promise<{ candles: Candle[]; demo: boolean }> {
  const params = new URLSearchParams({ interval: query.interval ?? "5m" });
  if (query.from != null && Number.isFinite(query.from)) params.set("from", String(Math.max(0, Math.floor(query.from))));
  const result = await getJson<Candle[]>(`/api/launches/${encodeURIComponent(address)}/candles?${params}`, demoCandles);
  return { candles: result.data, demo: result.demo };
}

export async function getCandles(address: string, query: CandleQuery = {}): Promise<Candle[]> {
  return (await getCandleSnapshot(address, query)).candles;
}

export async function getTradeActivity(address: string): Promise<ActivityItem[]> {
  const path = `/api/activity?token=${encodeURIComponent(address)}&kind=swaps&limit=40`;
  const fallback = demoActivity.filter((item) =>
    item.token.toLowerCase() === address.toLowerCase() && (item.side === "buy" || item.side === "sell")
  );
  const result = await getJson<Paginated<ActivityItem>>(path, { items: fallback, cursor: null, source: "demo" });
  return result.data.items;
}

export async function getHolders(address: string, fallbackTotal: number): Promise<HolderSnapshot> {
  const result = await getJson<HolderSnapshot>(`/api/launches/${encodeURIComponent(address)}/holders?limit=5`, {
    items: demoHolderPositions,
    total: fallbackTotal,
    source: "demo"
  });
  return result.data;
}

export async function getLiveActivity(): Promise<ActivityItem[]> {
  const result = await getJson<Paginated<ActivityItem>>("/api/activity?limit=40", {
    items: demoActivity,
    cursor: null,
    source: "demo"
  });
  return result.data.items.filter((item) => item.side === "buy" || item.side === "sell" || item.side === "launch");
}
